import React from "react"
import Moment from "moment"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { GetApi } from "../../api/api"
import { ProjectHeader as FeatureCatalogHeader, Roles, StreamVersions, CustomizationStatus } from "../../api/types"
import Pending from "../../components/Pending"
import AppContext from "../../components/AppContext"
import Table, { AddButton, ActionPopover, ActionPopoverButton } from "../../components/Table"
import Button from "../../components/Button"
import { LockBadge } from "../../components/Badge"

import { useHistory, useParams } from "react-router-dom"
import EditModal from "./components/EditModal"
import Panel from "./components/Panel"
import publishFcc from "./components/Publish"
import ShareModal from "./components/ShareModal"
import { CustomFeatureCatalog, ProcessStatus } from "../../api/generated/QuadriConfigurationApi"
import ImportProgressModal from "./components/ImportProgressModal"
import importFccFromExcel, { FccImportProgress } from "./components/FccImport"

interface RenderSuccess {
  featureCatalogs: FeatureCatalogHeader[]
  baseFeatureCatalogs: Array<{ name: string; id: string }>
}

type PendingType = Pending<RenderSuccess, any>

interface FeatureCatalogsState {
  showEditModal: boolean
  showShareModal: boolean
  disabled: boolean
  isNewFeatureCatalog?: boolean
  editFeatureCatalog?: FeatureCatalogHeader
  shareFeatureCatalogId?: string
  overrideSelectedId?: string
  publishedVersions: Array<string | undefined>
  customFeatureCatalogs: CustomFeatureCatalog[]
  importProgress: FccImportProgress | undefined
}

interface FeatureCatalogsProps {
  history: any // from React router
  id: string // from route params
  maximumStreamVersion?: string
}

class FeatureCatalogs extends React.Component<FeatureCatalogsProps, FeatureCatalogsState> {
  public static contextType = AppContext
  private pendingRef: React.RefObject<PendingType>
  private importInputRef: any

  constructor(props: FeatureCatalogsProps) {
    super(props)
    this.pendingRef = React.createRef<PendingType>()
    this.importInputRef = React.createRef();
    this.state = {
      showEditModal: false,
      showShareModal: false,
      disabled: false,
      publishedVersions: ["nothing"],
      customFeatureCatalogs: [],
      importProgress: undefined
    }
  }

  public componentDidMount() {
    if (this.props.id) {
      this.showPanel(this.props.id)
      this.forceHighlightRow(this.props.id)
    }
  }

  public componentDidUpdate(prevProps: FeatureCatalogsProps) {
    if (this.props.id !== prevProps.id) {
      const id = this.props.id
      if (!id) {
        // hide panel
      } else {
        this.showPanel(id)
      }
    }
  }

  public render() {
    const getData = async () => {
      try {
        const api = await GetApi()
        // concurrent async calls
        const [featureCatalogs, apiBaseFeatureCatalogs] = await Promise.all([
          api.getFeatureCatalogCustomizations(),
          api.getFeatureCatalogs()
        ])
        const baseFeatureCatalogs = apiBaseFeatureCatalogs.map((fc) => ({
          name: `${fc.name} ${fc.version} - ${fc.date}`,
          id: fc.id,
          maximumStreamVersion: fc.streamVersion
        }))

        return {
          featureCatalogs,
          baseFeatureCatalogs
        }
      } catch (error) {
        throw new Error(error as any)
      }
    }

    const renderSuccess = (props: RenderSuccess) => {
      const onRowClick = async (index: any) => {
        // Once the user has clicked a row, stop overriding the selected row

        this.setState({ overrideSelectedId: undefined })

        if (index !== -1) {
          const id = props.featureCatalogs[index].id!
          ;(this.props.history as any).push(`/featurecatalogs/${id}`)
        } else {
          ;(this.props.history as any).push(`/featurecatalogs`)
        }
      }

      let overrideSelectedRow = this.state.overrideSelectedId
        ? props.featureCatalogs.findIndex((fc) => fc.id === this.state.overrideSelectedId)
        : undefined

      if (overrideSelectedRow === -1) {
        overrideSelectedRow = undefined
      }

      const rowData = props.featureCatalogs.map((fcc) => {
        return {
          icon: (
            <span className="d-flex justify-content-center">
              <i className="modus-icons">map_layers</i>
            </span>
          ),
          name: fcc.currentVersionStatus === CustomizationStatus.Locked
            ? (<><LockBadge className="mr-2" variant="secondary" /><span>{fcc.name}</span></>)
            : fcc.name,
          role: this.getRole(fcc.myRole),
          currentversion: fcc.currentVersion,
          modified: fcc.modifiedAt,
          action: <this.EditButtons fcc={fcc} />
        }
      })

      const columnData = [
        {
          Header: "",
          accessor: "icon",
          width: 70,
          sortable: false
        },
        {
          Header: "Name",
          accessor: "name"
        },
        {
          Header: "Role",
          accessor: "role"
        },
        {
          Header: "Version",
          accessor: "currentversion"
        },
        {
          Header: "Modified",
          id: "modified",
          accessor: (row: any) => Moment(row.modified).format("x"),
          Cell: (row: any) => Moment(row.original.modified).format("lll")
        },
        {
          Header: <AddButton onClick={this.createFeatureCatalog} disabled={this.state.disabled} />,
          accessor: "action",
          width: 70,
          sortable: false
        }
      ]

      return (
        <Row className="d-flex flex-fill justify-content-center">
          {this.state.showEditModal && (
            <EditModal
              fccId={this.state.editFeatureCatalog!.id!}
              isOpen={this.state.showEditModal}
              toggle={this.toggleEditModal}
              headerText={this.state.isNewFeatureCatalog ? "New Feature Catalog" : "Edit Feature Catalog"}
              toggleText={this.state.isNewFeatureCatalog ? "Create" : "Done"}
              onCancel={
                this.state.isNewFeatureCatalog
                  ? () => this.deleteFeatureCatalog(this.state.editFeatureCatalog!)
                  : undefined
              }
              size="xl"
            />
          )}
          {this.state.showShareModal && (
            <ShareModal
              fccId={this.state.shareFeatureCatalogId!}
              isOpen={this.state.showShareModal}
              toggle={this.toggleShareModal}
              headerText="Share Feature Catalog"
              toggleText="Done"
            />
          )}
          {this.state.importProgress && (
            <ImportProgressModal
              isOpen={true}
              toggle={this.toggleImportProgressModal}
              fileName={this.state.importProgress.fileName}
              progress={this.state.importProgress.progress ?? 0}
              counters={this.state.importProgress.counters}
              status={this.state.importProgress.status ?? ProcessStatus.Running}
              message={this.state.importProgress.message ?? null}
              errors={this.state.importProgress.errors}
          />)}
          {rowData.length > 0 ? (
            <Col xs={12} className="mb-3">
              <Table
                data={rowData}
                columns={columnData}
                onRowClick={onRowClick}
                overrideSelected={overrideSelectedRow}
                defaultSorted={[
                  {
                    id: "modified",
                    desc: true
                  }
                ]}
              />
              <input
                type="file"
                accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                id="customizationExcelUpload"
                ref={this.importInputRef}
                disabled={this.state.disabled}
                onChange={this.onExcelImportFileSelected}
                style={{
                  display: "none"
                }} 
              />
            </Col>
          ) : (
            <Col sm={12} md={8} xl={5} className="my-auto text-center">
              <p className="display-4">Hi, there!</p>
              <p className="mb-4 text-muted">
                It seems like you haven't created a feature catalog yet, press the button to get started with your first
                feature catalog.
              </p>
              <Button variant="primary" onClick={this.createFeatureCatalog}>
                Get started
              </Button>
            </Col>
          )}
        </Row>
      )
    }

    return <Pending ref={this.pendingRef} getData={getData} renderSuccess={renderSuccess} />
  }

  private EditButtons = (props: { fcc: FeatureCatalogHeader }) => {
    const editFeatureCatalog = () => this.toggleEditModal(props.fcc)
    const shareFeatureCatalog = () => this.toggleShareModal(props.fcc.id!)
    const publishFeatureCatalog = () => this.publishFeatureCatalog(props.fcc)
    const deleteFeatureCatalog = () => this.deleteFeatureCatalog(props.fcc)
    const exportFeatureCatalogToExcel = () => this.exportFeatureCatalogToExcel(props.fcc)
    const selectExcelFileToImport = () => this.selectExcelFileToImport(props.fcc)

    return (
      <ActionPopover>
        <ActionPopoverButton onClick={editFeatureCatalog} disabled={this.state.disabled}>
          <span>Edit</span>
        </ActionPopoverButton>
        <div className="dropdown-divider" />
        <ActionPopoverButton onClick={shareFeatureCatalog} disabled={this.state.disabled}>
          <span>Share</span>
        </ActionPopoverButton>
        <ActionPopoverButton onClick={publishFeatureCatalog} disabled={this.state.disabled}>
          <span>Publish</span>
        </ActionPopoverButton>
        <ActionPopoverButton 
          onClick={exportFeatureCatalogToExcel} 
          disabled={this.state.disabled}>
          <span>Export to Excel</span>
        </ActionPopoverButton>
        <ActionPopoverButton 
          onClick={selectExcelFileToImport} 
          disabled={this.state.disabled}>
          <span>Import from Excel</span>
        </ActionPopoverButton>
        <div className="dropdown-divider" />
        <ActionPopoverButton
          variant="danger"
          onClick={deleteFeatureCatalog}
          disabled={this.state.disabled || props.fcc.currentVersion !== 1}
        >
          <span>Delete</span>
          {props.fcc.currentVersion !== 1 && <LockBadge className="ml-3" variant="danger" />}
        </ActionPopoverButton>
      </ActionPopover>
    )
  }

  private getRole(myRole?: Roles) {
    switch (myRole) {
      case Roles.Owner:
        return "Owner"
      case Roles.Editor:
        return "Editor"
      case Roles.Viewer:
        return "Viewer"
      default:
        return ""
    }
  }

  private updateFeatureCatalog = async (id: string) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      await api.getFeatureCatalogCustomization(id)
      this.refresh()
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private createFeatureCatalog = async () => {
    this.setDisabled(true)
    try {
      // Get list of possible base FCs and sort it so standard Quadri FCs comes first ordered by descending dates
      // Then use the first FC in the list as default base FC
      const api = await GetApi()
      const baseFeatureCatalogs = await api.getFeatureCatalogs(undefined, undefined, StreamVersions.SV_4110)
      if (baseFeatureCatalogs.length > 0) {
        const quadriStd = "QuadriDCM"
        const defaultBaseFeatureCatalogId = baseFeatureCatalogs.sort((a, b) =>
          a.standard === quadriStd && b.standard !== quadriStd 
            ? -1
            : a.standard !== quadriStd && b.standard === quadriStd
              ? 1
              : new Date(a.date) < new Date(b.date) 
                ? 1 
                : -1
        )[0].id
        const featureCatalog = await api.createFeatureCatalogCustomization({
          name: "NewFeatureCatalog",
          description: "",
          streamVersion: StreamVersions.SV_4211,
          baseFeatureCatalogs: [{ id: defaultBaseFeatureCatalogId }]
        })
        this.toggleEditModal(
          {
            ...featureCatalog,
            id: featureCatalog.customizationId!
          },
          true
        )
      } else {
        throw new Error("Cannot create new feature catalog since there are no base catalogs available.")
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private deleteFeatureCatalog = async (fcc: FeatureCatalogHeader) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      if (this.state.showEditModal === true) {
        this.toggleEditModal(fcc, true)
        await api.deleteFeatureCatalogCustomization(fcc.id!)
        this.refresh()
      } else {
        if (window.confirm("Are you sure you want to delete this feature catalog?")) {
          await this.context.setPanelContent(undefined)
          await api.deleteFeatureCatalogCustomization(fcc.id!)
          this.refresh()
          this.context.useToasts.addToast(`Successfully deleted the feature catalog '${fcc.name}'.`, "success", true)
        }
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private exportFeatureCatalogToExcel = async (fcc: FeatureCatalogHeader) => {
    this.setDisabled(true)
    try {
      // Retrieve Excel file from API
      const api = await GetApi()
      const result = await api.exportFeatureCatalogCustomizationToExcel(fcc.id!)

      // Create a link pointing to the ObjectURL containing the blob. 
      const a = document.createElement("a")
      document.body.appendChild(a)
      a.setAttribute("style", "display: none")
      const url = window.URL.createObjectURL(result.data)
      a.href = url
      
      // Use filename returned from API if present; otherwise create a sensible one
      a.download = result.fileName === undefined ? `${fcc.name}.${fcc.currentVersion}.xlsx` : result.fileName!

      // Start download
      a.click()

      // Cleanup and remove the link
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      this.refresh()
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private selectExcelFileToImport = async (fcc: FeatureCatalogHeader) => {
    console.log(`Select Excel file to import into FCC ${fcc.name}`)

    // Small hack: use the name of the input element to store the id of the FCC to import into
    this.importInputRef.current.name = `${fcc.id}`

    this.importInputRef.current.click()
  }

  private onExcelImportFileSelected = async (e: any) => {

    // Read the fcc ID from the name of the input element
    const fccId = e.target.name
    console.log("onExcelImportFileSelected", e.target)
    const inputFile = e.target.files[0]
    e.target.value = null // In order to reset the input file field
    e.target.name = ""
    this.setDisabled(true)
    try {
      await importFccFromExcel(fccId, inputFile, this.updateImportProgress)

      this.refresh()
      this.context.useToasts.addToast(`Successfully imported Excel file '${inputFile.name}' into feature catalog.`, "success", true)
    } catch (error) {
      this.context.useToasts.addToast(error, "danger", true)
    } finally {
      this.setDisabled(false)
    }    
  }
  
  private updateImportProgress = (progress: FccImportProgress | undefined) => {
    this.setState(
      (prevState) => ({
        importProgress: progress
      })
    )
  }

  private publishFeatureCatalog = async (fcc: FeatureCatalogHeader) => {
    this.setDisabled(true)
    try {
      if (
        window.confirm(
          "Are you sure you want to publish? It is not possible to remove properties from a published feature catalog."
        )
      ) {
        await publishFcc(fcc.id!)
        this.refresh()
        this.context.useToasts.addToast(`Successfully published the feature catalog '${fcc.name}'.`, "success", true)
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private toggleEditModal = (fcc?: FeatureCatalogHeader, isNew: boolean = false) => {
    this.setState(
      (prevState) => ({
        isNewFeatureCatalog: isNew,
        editFeatureCatalog: fcc,
        showEditModal: !prevState.showEditModal
      }),
      () => {
        if (this.state.showEditModal === false && !this.state.isNewFeatureCatalog) {
          this.refresh()
        }
      }
    )
  }

  private toggleShareModal = (id?: string) => {
    this.setState(
      (prevState) => ({
        shareFeatureCatalogId: id,
        showShareModal: !prevState.showShareModal
      }),
      () => {
        if (this.state.showShareModal === false) {
          this.refresh()
        }
      }
    )
  }

  private toggleImportProgressModal = () => {
    this.setState(
      (prevState) => ({
        importProgress: undefined
      })
    )
  }

  private setDisabled = (value: boolean) => {
    this.setState({
      disabled: value
    })
  }

  private refresh() {
    this.pendingRef?.current?.refresh()
  }

  private showPanel(id: string) {
    const panel = () => <Panel id={id} onRefresh={this.updateFeatureCatalog} />
    this.context.setPanelContent(panel)
  }

  private forceHighlightRow(id: string) {
    this.setState({ overrideSelectedId: id })
  }
}

const FeatureCatalogsRoute = (props: {}) => {
  const { id } = useParams() as any
  const { maximumStreamVersion } = useParams() as any
  const history = useHistory()
  return <FeatureCatalogs history={history} id={id} maximumStreamVersion={maximumStreamVersion} />
}

export default FeatureCatalogsRoute
